<template>
    <base-sheet
        id="massive-send-sidebar"
        width="lg"
        static-backdrop
        :title="sheetTitle"
        :hide-cancel="displayMessageDetail"
        :ok-text="okText"
        :ok-disabled="!displayMessageDetail && activeSaveBtn"
        :busy="loading"
        @ok="submit()"
        @hidden="resetMassiveSend()"
    >
        <div v-if="displayMessageDetail" class="d-flex flex-column justify-content-start">
            <div class="name-content d-flex flex-column mb-5">
                <h6 class="text-black text-left font-nunito fs-14">{{ $t('name') }}</h6>
                <span class="text-dark-brown text-left font-nunito fs-13">
                    {{ selectedMassMessage.name }}
                </span>
            </div>
            <div class="name-content d-flex flex-column mb-5">
                <h6 class="text-black text-left font-nunito fs-14">Message</h6>
                <span class="text-dark-brown text-left font-nunito fs-13">
                    {{ selectedMassMessage.content }}
                </span>
            </div>
            <div class="name-content d-flex flex-column mb-5">
                <div class="d-flex align-items-center mb-2">
                    <h6 class="text-black text-left font-nunito fs-14 m-0">{{ $t('audience') }}</h6>
                    <span class="font-italic text-dark-brown font-nunito fs-13 ml-2">({{ selectedMassMessage.audienceCount }} contacts)</span>
                </div>
                <div v-if="selectedMassMessage.audiences !== null">
                    <div v-for="tag in selectedMassMessage.audiences" :key="tag.id"
                        class="d-flex d-inline-flex flex-row p-0 mr-2 tags">
                        <p class="font-nunito fs-13">{{ tag }}</p>
                    </div>
                    <div class="text-left text-dark-grey font-italic">
                        <span class="font-italic text-grey-9 font-nunito fs-13">
                            ({{ selectedMassMessage.sentCount ? selectedMassMessage.sentCount : 0 }} {{ $tc('sent', selectedMassMessage.sentCount) }},
                            {{ selectedMassMessage.failedCount ? selectedMassMessage.failedCount : 0 }} {{ $tc('failed', selectedMassMessage.failedCount) }})
                        </span>
                    </div>
                </div>
                <div v-else class="d-flex flex-column">
                    <span class="text-content font-nunito fs-13">{{ $t('all') }}</span>
                    <span class="font-italic text-content text-grey-9 font-nunito">
                        ({{ selectedMassMessage.sentCount ? selectedMassMessage.sentCount : 0 }} {{ $tc('sent', selectedMassMessage.sentCount) }},
                        {{ selectedMassMessage.failedCount ? selectedMassMessage.failedCount : 0 }} {{ $tc('failed', selectedMassMessage.failedCount) }})
                    </span>
                </div>
            </div>
            <div class="name-content d-flex flex-column mb-2">
                <h6 class="font-nunito fs-14">{{ $t('time-date-to-send') }}</h6>
                <div class="d-flex d-inline-flex">
                    <span class="text-dark-brown text-left mr-5 font-nunito fs-13">
                        {{ fromUtc(selectedMassMessage.sentAt).date }} {{ fromUtc(selectedMassMessage.sentAt).time }} <timezone></timezone>
                    </span>
                </div>
            </div>
        </div>
        <div v-else>
            <form>
                <!-- Name -->
                <div class="form-group mb-5">
                    <label class="font-nunito-SemiBold fs-14 text-dark" for="name">{{ $t('name') }}</label>
                    <input
                        type="text"
                        name="name"
                        class="form-control form-border font-nunito"
                        :placeholder="$t('template-name-placeholder')"
                        v-model.trim="massMessage.name">
                </div>
                <!-- Message -->
                <div class="form-group" :class="{ 'mb-5': !errors.has('content') }">
                    <label class="font-nunito-SemiBold fs-14 text-dark"  for="name">Message</label>
                    <!-- Gabarits -->
                    <select-dropdown
                        :options="options"
                        :selected="selectedTemplate"
                        v-on:selectTemplate="chooseTemplate($event)"
                        :showLabel="false"
                        :type="'template'">
                    </select-dropdown>
                    <textarea
                        type="text"
                        name="message"
                        class="form-control form-border font-nunitor mt-2"
                        :placeholder="$t('template-text-placeholder')"
                        v-model.trim="massMessage.content"
                        v-validate="'max:1600'"
                        data-vv-as="Message"
                        data-vv-name="content"
                        :class="{ 'is-invalid': submitted && errors.has('content') }">
                    </textarea>
                    <sms-counter :sms="massMessage.content" v-on:limitExceeded="smsLimitExceeded = $event"></sms-counter>
                </div>
                <div class="invalid-feedback text-left mb-5 font-nunito"
                    v-if="submitted && errors.has('content')">
                    {{ errors.first('content') }}
                </div>

                <!-- Audiences -->
                <div class="form-group mb-4">
                    <div class="d-flex flex-column">
                        <label class="font-nunito-SemiBold fs-14 text-dark" for="audience">{{ $t('audience') }}</label>
                        <small class="text-left text-black font-nunito">{{ $t('no-tags-selected') }}</small>
                    </div>
                    <base-chips-input
                        class="mt-2"
                        v-model="massMessage.audiences"
                        :suggestions="tagNames"
                        :allow-create="false"
                        :placeholder="$t('add-tag')"
                        label="Audience(s)"
                    ></base-chips-input>
                </div>

                <!-- Planned Date & Time -->
                <div class="date-time-to-send-section form-group mb-5">
                    <div class="d-flex flex-column">
                        <label class="font-nunito-SemiBold  fs-14 text-dark mb-0" for="date-time">
                            {{ $t('time-date-to-send') }}
                        </label>
                        <small class="text-left text-black font-nunito">
                            {{ $t('schedule-time-to-send') }}
                        </small>
                        <small class="btn-link text-decoration"><timezone>{{ $t('learn-about-utc') }}</timezone></small>
                    </div>
                    <div class="row mt-1">
                        <div class="col">
                            <label for="planned-date" class="font-nunito fs-12 text-darker-grey mb-0">
                                {{ $t('Date') }}
                            </label>
                            <input type="date" class="form-border"
                                name="planned-date"
                                v-model="massMessage['planned-date']">
                            <div class="invalid-feedback" v-if="submitted && errors.has('planned-date')">
                                {{ errors.first('planned-date') }}
                            </div>
                        </div>
                        <div class="col">
                            <label for="planned-time" class="font-nunito fs-12 text-darker-grey mb-0">
                                {{ $t('utc-time') }}
                            </label>
                            <input type="time" class="form-border"
                                name="planned-time"
                                v-model="massMessage['planned-time']">
                            <div class="invalid-feedback" v-if="submitted && errors.has('planned-time')">
                                {{ errors.first('planned-time') }}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <div class="d-flex justify-content-center" v-if="editMessage">
                <button class="btn rounded-pill bg-white px-4 send-now-button fs-14 text-brown"
                    :disabled="sendBtnDisabled"
                    @click="sendSMS()">
                    <span class="spinner-border spinner-small text-black" v-if="sendingMessage"></span>
                    <font-awesome-icon v-else class="text-dark-pink mr-2" :icon="['fas', 'paper-plane']"/>
                    <span class="text-black" v-if="sendingMessage">
                        {{ $t('sending-in-progress') }}
                    </span>
                    <span class="fs-14 text-brown" v-else>
                        {{ $t('send-now') }}
                    </span>
                </button>
            </div>
        </div>
    </base-sheet>
</template>

<script>

import Vue from 'vue';

// Import internal libraries
import Alert    from '@/helpers/Alert.js';
import Timezone from '@/core/components/Timezone.vue';
import Format   from '@/helpers/Format.js';

import ReplaceMessageMixin from '@/core/mixins/ReplaceMessageMixin';

// Import Components
import SelectDropdown from '@/core/components/SelectDropdown.vue';
import SmsCounter     from '@/connectors/sms/components/SmsCounter.vue';
import BaseSheet      from '@/core/components/BaseSheet.vue';
import BaseChipsInput from '@/core/components/BaseChipsInput.vue';

// Import external libraries
import {
    mapActions,
    mapState,
    mapGetters
} from 'vuex';
const clone = require('rfdc')();
import moment from 'moment-timezone';

export default {
    mixins:     [ReplaceMessageMixin],
    props:      ['editMessage', 'displayMessageDetail'],
    components: {
        BaseSheet,
        BaseChipsInput,
        Timezone,
        SelectDropdown,
        SmsCounter
    },

    created () {
        this.getTags();
    },

    mounted () {
        // Get the temaplate
        this.getTemplate().then(() => {
            this.options.push({
                'id':      null,
                'name':    this.$i18n.t('choose-template'),
                'content': ''
            });
            for (const templateId in this.templates) {
                this.options.push({
                    'id':      templateId,
                    'name':    this.templates[templateId].attributes.name,
                    'content': this.templates[templateId].attributes.content
                });
            }
        });
    },

    data () {
        const MASS_CATEGORY  = 'mass';

        return {
            selectedTemplate: this.$i18n.t('choose-template'),
            tags:             [],
            disabled:         false,
            smsLimitExceeded: false,
            loading:          false,
            submitted:        false,
            sendingMessage:   false,
            massMessage:      {
                audienceCount:  '',
                audiences:      [],
                content:        '',
                name:           '',
                category:       MASS_CATEGORY,
                'planned-date': '',
                'planned-time': ''
            },
            options: []
        };
    },

    computed: {
        ...mapState('core/scheduled-messages', {
            massMessages:        'plannedScheduledMessages',
            selectedMassMessage: 'selectedScheduledMessage'
        }),

        ...mapGetters('core/tags', { 'allTags': 'tags' }),

        tagNames () {
            return Object.values(this.allTags || {}).map(tag => tag.attributes.name);
        },

        /**
         * Load templates
         */
        ...mapState('core/templates', { 'templates': 'templates' }),

        /**
         * Get the min date (yesterday)
         */
        minDate () {
            return moment().subtract(1, 'days')
                .format('yyyy-MM-DD')
                .toString();
        },

        /**
         * Validates the planned date
         *
         * @return boolean
         */
        dateRules () {
            return this.massMessage['planned-time'] ? '|required' : '';
        },

        /**
         * Validates the planned time
         *
         * @return boolean
         */
        timeRules () {
            return this.massMessage['planned-date'] ? '|required' : '';
        },

        sendBtnDisabled () {
            return this.errors.any() || (this.massMessage.name === '' || this.massMessage.content === '');
        },

        /**
         * Test message name and content to activate add/edit button
         *
         * @return boolean
         */
        sheetTitle () {
            if (this.displayMessageDetail) {
                return this.$t('massive-sent-details');
            }

            return this.editMessage ? this.$t('edit-massive-send') : this.$t('add-massive-send');
        },

        okText () {
            if (this.displayMessageDetail) {
                return this.$t('close');
            }

            return this.editMessage ? this.$t('save') : this.$t('add');
        },

        activeSaveBtn () {
            let activateSaveBtn;

            if (this.loading || this.sendingMessage) {
                activateSaveBtn = false;
            }

            if (this.massMessage.name === '' || this.massMessage.content === '' || this.smsLimitExceeded) {
                activateSaveBtn = true;
            } else {
                // Compare the text entered with existed to activate button
                activateSaveBtn = false;
            }
            return activateSaveBtn;
        },

        currentLang () {
            return this.$i18n.locale;
        }
    },

    watch: {
        currentLang () {
            this.selectedTemplate = this.$i18n.t('choose-template');
            this.options.map(option => {
                if (option.id === null) {
                    option.name = this.$i18n.t('choose-template');
                }
            });
        },

        /**
         * Selected mass message watcher
         *
         * @param      {Object}  value   The value
         */
        selectedMassMessage (value) {
            this.massMessage.audiences = clone(value.audiences);
            this.massMessage.content = clone(value.content);
            this.massMessage.name = clone(value.name);

            if (value.plannedDate !== '' && value.plannedTime !== '') {
                const date = value.plannedDate && value.plannedTime ? Format.fromUtc(`${value.plannedDate} ${value.plannedTime}`) : null;
                this.massMessage['planned-date'] = date ? date.date : null;
                this.massMessage['planned-time'] = date ? date.time : null;
            }
        }
    },

    methods: {
        /**
         * Load templates
         */
        ...mapActions('core/templates', { getTemplate: 'index' }),

        ...mapActions('core/scheduled-messages', {
            create:                   'create',
            update:                   'update',
            send:                     'send',
            resetSelectedMassMessage: 'resetSelectedScheduledMessage'
        }),

        ...mapActions('core/tags', { getTags: 'index' }),

        /**
         * Helper method to format date to use in template
         */
        fromUtc: Format.fromUtc,

        /**
         * Update selected option
         *
         * @return    void
         */
        chooseTemplate (option) {
            if (option.id === null) {
                this.selectedTemplate    = option.name;
                this.disabled            = option.id === null;
            } else if (this.massMessage.content === '') {
                this.selectedTemplate    = option.name;
                this.massMessage.content = option.content;
                this.disabled            = option.id === null;
            } else {
                this.confirmReplaceMessage()
                    .then((confirmed) => {
                        if (confirmed) {
                            this.selectedTemplate = option.name;
                            this.massMessage.content = option.content;
                        }
                    });
            }
        },

        /**
         *  Add a massive message
         */
        addMassMessage () {
            Vue.prototype.$validation = this.$validator;

            // Spinner on and disable button
            this.loading = true;
            this.disabled = true;
            this.submitted = true;

            this.$validator.validate().then((valid) => {
                if (!valid) {
                    this.loading = false;
                    return;
                }

                // Format date to UTC
                const date = this.massMessage['planned-date'] && this.massMessage['planned-time'] ?
                    Format.toUtc(`${this.massMessage['planned-date']} ${this.massMessage['planned-time']}`) : null;
                const attributes = clone(this.massMessage);
                attributes['planned-date'] = date ? date.date : null;
                attributes['planned-time'] = date ? date.time : null;

                this.create({ attributes })
                    .then(() => {
                        this.loading = false;

                        this.resetSelectedMassMessage();
                        this.$emit('massListUpdated');
                        Alert.success(this.$i18n.t('massive-send-created'));
                        this.clearMassiveSend();
                    })
                    .catch(() => {
                        this.loading = false;
                    });
            });
        },

        /**
         *  Edit the selected massive message
         */
        editMassMessage () {
            Vue.prototype.$validation = this.$validator;

            // Spinner on and disable button
            this.loading = true;
            this.disabled = true;
            this.submitted = true;

            this.$validator.validate().then((valid) => {
                if (!valid) {
                    this.loading = false;
                    return;
                }

                // Format date to UTC
                const date = this.massMessage['planned-date'] && this.massMessage['planned-time'] ?
                    Format.toUtc(`${this.massMessage['planned-date']} ${this.massMessage['planned-time']}`) : null;
                const attributes = clone(this.massMessage);
                attributes['planned-date'] = date ? date.date : null;
                attributes['planned-time'] = date ? date.time : null;

                this.update({
                    id: this.selectedMassMessage.id,
                    attributes
                })
                    .then(() => {
                        this.loading = false;
                        Alert.success(this.$i18n.t('massive-send-updated'));
                        this.clearMassiveSend();
                    })
                    .catch(() => {
                        this.loading = false;
                    });
            });
        },

        /**
         * Send immediately a massive message
         */
        sendSMS () {
            Vue.prototype.$validation = this.$validator;

            // Disable button
            this.disabled = true;
            this.submitted = true;

            this.$validator.validate().then((valid) => {
                if (!valid) {
                    this.loading = false;
                    return;
                }

                this.$dialog.confirm({
                    title:       this.$t('title-mass-message-send'),
                    text:        this.$t('confirm-scheduled-send'),
                    centerText:  true,
                    image:       '/images/confirm-send-scheduled-message.png',
                    confirmText: this.$t('send-scheduled-message')
                })
                    .then((confirmed) => {
                        if (!confirmed) {
                            return;
                        }
                        this.loading = true;
                        this.sendingMessage = true;
                        this.update({
                            id:         this.selectedMassMessage.id,
                            attributes: this.massMessage
                        })
                            .then(() => {
                                this.send({ id: this.selectedMassMessage.id })
                                    .then(() => {
                                        this.loading = false;
                                        this.sendingMessage = false;

                                        this.resetSelectedMassMessage();
                                        Alert.success(this.$i18n.t('scheduled-send-success'));
                                        this.clearMassiveSend();
                                    })
                                    .catch(() => {
                                        this.loading = false;
                                    });
                            })
                            .catch(() => {
                                this.loading = false;
                            });
                    });
            });
        },

        /**
         * Cancel add or edit massive message
         * Reset values to default
         */
        clearMassiveSend () {
            this.$bvModal.hide('massive-send-sidebar');
        },

        submit () {
            if (this.displayMessageDetail) {
                this.clearMassiveSend();
                return;
            }

            if (this.editMessage) {
                this.editMassMessage();
            } else {
                this.addMassMessage();
            }
        },

        resetMassiveSend () {
            if (this.displayMessageDetail) {
                return;
            }

            this.loading = false;
            this.resetSelectedMassMessage();
        }
    }
};
</script>

